/**
 * Pipeline symbol catalog rows: [kind, label, category].
 *
 * To add a symbol:
 * 1. Append a row here
 * 2. Map kind → family in familyMap.ts
 * 3. Ensure a drawer exists for that family (pipelineIcons.ts / future drawers/*)
 * 4. Optional: add aliases in aliases.ts
 */
import type { PipelineCatalogRow } from './types';

export const PIPELINE_CATALOG: PipelineCatalogRow[] = [
  // flow
  ['start', 'Start', 'flow'],
  ['end', 'End', 'flow'],
  ['process', 'Process', 'flow'],
  ['subprocess', 'Subprocess', 'flow'],
  ['activity', 'Activity', 'flow'],
  ['operation', 'Operation', 'flow'],
  ['task', 'Task', 'flow'],
  ['serviceTask', 'Service Task', 'flow'],
  ['userTask', 'User Task', 'flow'],
  ['manualTask', 'Manual Task', 'flow'],
  ['scriptTask', 'Script Task', 'flow'],
  ['businessRule', 'Business Rule', 'flow'],
  ['connector', 'Connector', 'flow'],
  ['arrow', 'Arrow', 'flow'],
  ['flow', 'Flow', 'flow'],
  ['pipeline', 'Pipeline', 'flow'],
  ['loop', 'Loop', 'flow'],
  ['retry', 'Retry', 'flow'],
  ['delay', 'Delay', 'flow'],
  ['wait', 'Wait', 'flow'],
  ['synchronization', 'Synchronization', 'flow'],
  ['dependency', 'Dependency', 'flow'],
  ['reference', 'Reference', 'flow'],
  ['link', 'Link', 'flow'],
  ['annotation', 'Annotation', 'flow'],
  ['comment', 'Comment', 'flow'],
  ['workflow', 'Workflow', 'flow'],
  ['businessProcess', 'Business Process', 'flow'],
  ['manufacturingProcess', 'Manufacturing Process', 'flow'],
  ['engineeringProcess', 'Engineering Process', 'flow'],
  ['softwareProcess', 'Software Process', 'flow'],
  ['valueStream', 'Value Stream', 'flow'],
  ['automation', 'Automation', 'flow'],
  // gateway
  ['decision', 'Decision', 'gateway'],
  ['gateway', 'Gateway', 'gateway'],
  ['exclusiveGateway', 'Exclusive Gateway', 'gateway'],
  ['parallelGateway', 'Parallel Gateway', 'gateway'],
  ['inclusiveGateway', 'Inclusive Gateway', 'gateway'],
  ['merge', 'Merge', 'gateway'],
  ['fork', 'Fork', 'gateway'],
  ['join', 'Join', 'gateway'],
  ['split', 'Split', 'gateway'],
  ['decisionPoint', 'Decision Point', 'gateway'],
  // event
  ['event', 'Event', 'event'],
  ['timer', 'Timer', 'event'],
  ['message', 'Message', 'event'],
  ['signal', 'Signal', 'event'],
  ['error', 'Error', 'event'],
  ['exception', 'Exception', 'event'],
  ['trigger', 'Trigger', 'event'],
  ['escalation', 'Escalation', 'event'],
  // io
  ['input', 'Input', 'io'],
  ['output', 'Output', 'io'],
  ['source', 'Source', 'io'],
  ['destination', 'Destination', 'io'],
  // queue
  ['queue', 'Queue', 'queue'],
  ['buffer', 'Buffer', 'queue'],
  // data
  ['document', 'Document', 'data'],
  ['folder', 'Folder', 'data'],
  ['file', 'File', 'data'],
  ['database', 'Database', 'data'],
  ['dataStore', 'Data Store', 'data'],
  ['dataObject', 'Data Object', 'data'],
  ['dataset', 'Dataset', 'data'],
  ['report', 'Report', 'data'],
  ['dashboard', 'Dashboard', 'data'],
  ['log', 'Log', 'data'],
  ['metric', 'Metric', 'data'],
  ['kpi', 'KPI', 'data'],
  ['analytics', 'Analytics', 'data'],
  // notify
  ['alert', 'Alert', 'notify'],
  ['notification', 'Notification', 'notify'],
  ['email', 'Email', 'notify'],
  ['phone', 'Phone', 'notify'],
  ['chat', 'Chat', 'notify'],
  // integration
  ['api', 'API', 'integration'],
  ['webhook', 'Webhook', 'integration'],
  ['eventBus', 'Event Bus', 'integration'],
  ['messageQueue', 'Message Queue', 'integration'],
  ['broker', 'Broker', 'integration'],
  ['externalSystem', 'External System', 'integration'],
  ['thirdPartyService', 'Third-Party Service', 'integration'],
  ['internet', 'Internet', 'integration'],
  // software
  ['service', 'Service', 'software'],
  ['microservice', 'Microservice', 'software'],
  ['module', 'Module', 'software'],
  ['package', 'Package', 'software'],
  ['library', 'Library', 'software'],
  ['repository', 'Repository', 'software'],
  ['branch', 'Branch', 'software'],
  ['mergeRequest', 'Merge Request', 'software'],
  ['pullRequest', 'Pull Request', 'software'],
  // cicd
  ['cicd', 'CI/CD', 'cicd'],
  ['build', 'Build', 'cicd'],
  ['compile', 'Compile', 'cicd'],
  ['artifact', 'Artifact', 'cicd'],
  ['deploy', 'Deploy', 'cicd'],
  ['release', 'Release', 'cicd'],
  ['rollback', 'Rollback', 'cicd'],
  ['environment', 'Environment', 'cicd'],
  ['development', 'Development', 'cicd'],
  ['testing', 'Testing', 'cicd'],
  ['qa', 'QA', 'cicd'],
  ['staging', 'Staging', 'cicd'],
  ['production', 'Production', 'cicd'],
  ['monitoring', 'Monitoring', 'cicd'],
  // security
  ['authentication', 'Authentication', 'security'],
  ['authorization', 'Authorization', 'security'],
  ['encryption', 'Encryption', 'security'],
  ['firewall', 'Firewall', 'security'],
  ['security', 'Security', 'security'],
  ['secretVault', 'Secret Vault', 'security'],
  // people
  ['user', 'User', 'people'],
  ['customer', 'Customer', 'people'],
  ['client', 'Client', 'people'],
  ['operator', 'Operator', 'people'],
  ['engineer', 'Engineer', 'people'],
  ['technician', 'Technician', 'people'],
  ['manager', 'Manager', 'people'],
  ['team', 'Team', 'people'],
  ['department', 'Department', 'people'],
  ['vendor', 'Vendor', 'people'],
  ['supplier', 'Supplier', 'people'],
  ['stakeholder', 'Stakeholder', 'people'],
  // governance
  ['approval', 'Approval', 'governance'],
  ['review', 'Review', 'governance'],
  ['validation', 'Validation', 'governance'],
  ['verification', 'Verification', 'governance'],
  ['inspection', 'Inspection', 'governance'],
  ['audit', 'Audit', 'governance'],
  ['signOff', 'Sign-off', 'governance'],
  ['requirement', 'Requirement', 'governance'],
  ['specification', 'Specification', 'governance'],
  ['standard', 'Standard', 'governance'],
  ['policy', 'Policy', 'governance'],
  ['compliance', 'Compliance', 'governance'],
  ['certification', 'Certification', 'governance'],
  // project
  ['goal', 'Goal', 'project'],
  ['objective', 'Objective', 'project'],
  ['scope', 'Scope', 'project'],
  ['charter', 'Charter', 'project'],
  ['milestone', 'Milestone', 'project'],
  ['phase', 'Phase', 'project'],
  ['sprint', 'Sprint', 'project'],
  ['timeline', 'Timeline', 'project'],
  ['calendar', 'Calendar', 'project'],
  ['schedule', 'Schedule', 'project'],
  ['project', 'Project', 'project'],
  ['program', 'Program', 'project'],
  ['portfolio', 'Portfolio', 'project'],
  ['budget', 'Budget', 'project'],
  ['cost', 'Cost', 'project'],
  ['expense', 'Expense', 'project'],
  ['invoice', 'Invoice', 'project'],
  ['payment', 'Payment', 'project'],
  ['risk', 'Risk', 'project'],
  ['issue', 'Issue', 'project'],
  ['blocker', 'Blocker', 'project'],
  ['changeRequest', 'Change Request', 'project'],
  ['changeOrder', 'Change Order', 'project'],
  ['version', 'Version', 'project'],
  ['revision', 'Revision', 'project'],
  ['configuration', 'Configuration', 'project'],
  ['asset', 'Asset', 'project'],
  ['resource', 'Resource', 'project'],
  // manufacturing
  ['machine', 'Machine', 'manufacturing'],
  ['workstation', 'Workstation', 'manufacturing'],
  ['robot', 'Robot', 'manufacturing'],
  ['conveyor', 'Conveyor', 'manufacturing'],
  ['assemblyStation', 'Assembly Station', 'manufacturing'],
  ['cncMachine', 'CNC Machine', 'manufacturing'],
  ['productionLine', 'Production Line', 'manufacturing'],
  ['manufacturingCell', 'Manufacturing Cell', 'manufacturing'],
  ['warehouse', 'Warehouse', 'manufacturing'],
  ['inventory', 'Inventory', 'manufacturing'],
  ['storage', 'Storage', 'manufacturing'],
  ['rawMaterial', 'Raw Material', 'manufacturing'],
  ['workInProgress', 'Work In Progress (WIP)', 'manufacturing'],
  ['finishedGoods', 'Finished Goods', 'manufacturing'],
  ['scrap', 'Scrap', 'manufacturing'],
  ['waste', 'Waste', 'manufacturing'],
  ['rework', 'Rework', 'manufacturing'],
  ['packaging', 'Packaging', 'manufacturing'],
  ['pallet', 'Pallet', 'manufacturing'],
  ['cargoContainer', 'Container', 'manufacturing'],
  // logistics
  ['truck', 'Truck', 'logistics'],
  ['loadingDock', 'Loading Dock', 'logistics'],
  ['forklift', 'Forklift', 'logistics'],
  ['agv', 'AGV', 'logistics'],
  ['crane', 'Crane', 'logistics'],
  ['customerDelivery', 'Customer Delivery', 'logistics'],
  ['handover', 'Handover', 'logistics'],
  ['closure', 'Closure', 'logistics'],
  // quality
  ['maintenance', 'Maintenance', 'quality'],
  ['repair', 'Repair', 'quality'],
  ['calibration', 'Calibration', 'quality'],
  ['measurement', 'Measurement', 'quality'],
  ['qualityControl', 'Quality Control', 'quality'],
  ['qualityAssurance', 'Quality Assurance', 'quality'],
  ['testBench', 'Test Bench', 'quality'],
  ['functionalTest', 'Functional Test', 'quality'],
  ['integrationTest', 'Integration Test', 'quality'],
  ['unitTest', 'Unit Test', 'quality'],
  ['performanceTest', 'Performance Test', 'quality'],
  ['reliabilityTest', 'Reliability Test', 'quality'],
  ['acceptanceTest', 'Acceptance Test', 'quality'],
  ['prototype', 'Prototype', 'quality'],
  ['simulation', 'Simulation', 'quality'],
  ['cadModel', 'CAD Model', 'quality'],
  ['schematic', 'Schematic', 'quality'],
  ['blockDiagram', 'Block Diagram', 'quality'],
  ['bom', 'BOM', 'quality'],
  // industrial
  ['plc', 'PLC', 'industrial'],
  ['hmi', 'HMI', 'industrial'],
  ['scada', 'SCADA', 'industrial'],
  ['mes', 'MES', 'industrial'],
  ['erp', 'ERP', 'industrial'],
  ['crm', 'CRM', 'industrial'],
  ['hrms', 'HRMS', 'industrial'],
  ['sensor', 'Sensor', 'industrial'],
  ['camera', 'Camera', 'industrial'],
  ['scanner', 'Scanner', 'industrial'],
  ['barcodeReader', 'Barcode Reader', 'industrial'],
  ['rfidReader', 'RFID Reader', 'industrial'],
  ['iotDevice', 'IoT Device', 'industrial'],
  // cloud
  ['cloud', 'Cloud', 'cloud'],
  ['server', 'Server', 'cloud'],
  ['virtualMachine', 'Virtual Machine', 'cloud'],
  ['appContainer', 'App Container', 'cloud'],
  ['kubernetesCluster', 'Kubernetes Cluster', 'cloud'],
  ['edgeDevice', 'Edge Device', 'cloud'],
  ['storageSystem', 'Storage System', 'cloud'],
  ['backup', 'Backup', 'cloud'],
  ['archive', 'Archive', 'cloud'],
  ['archiveStorage', 'Archive Storage', 'cloud'],
  // ai
  ['aiAgent', 'AI Agent', 'ai'],
  // status
  ['controlPoint', 'Control Point', 'status'],
  ['checkpoint', 'Checkpoint', 'status'],
  ['success', 'Success', 'status'],
  ['failure', 'Failure', 'status'],
  ['warning', 'Warning', 'status'],
  ['information', 'Information', 'status'],
  ['priority', 'Priority', 'status'],
  // layout
  ['tag', 'Tag', 'layout'],
  ['label', 'Label', 'layout'],
  ['group', 'Group', 'layout'],
  ['swimlane', 'Swimlane', 'layout'],
  ['lane', 'Lane', 'layout'],
  ['pool', 'Pool', 'layout'],
  ['region', 'Region', 'layout'],
  ['zone', 'Zone', 'layout'],
  // facilities
  ['factory', 'Factory', 'facilities'],
  ['building', 'Building', 'facilities'],
  ['home', 'Home', 'facilities'],
  ['school', 'School', 'facilities'],
  ['stadium', 'Stadium', 'facilities'],
  ['shop', 'Shop', 'facilities'],
  ['market', 'Market', 'facilities'],
  ['park', 'Park', 'facilities'],
  ['parking', 'Parking', 'facilities'],
  ['temple', 'Temple', 'facilities'],
  ['staircase', 'Staircase', 'facilities'],
  ['lift', 'Lift', 'facilities'],
  ['window', 'Window', 'facilities'],
  // transport
  ['car', 'Car', 'transport'],
  ['van', 'Van', 'transport'],
  ['bike', 'Bike', 'transport'],
  ['cycle', 'Cycle', 'transport'],
  ['rickshaw', 'Rickshaw', 'transport'],
  ['autoVehicle', 'Auto Vehicle', 'transport'],
  ['bus', 'Bus', 'transport'],
  ['ship', 'Ship', 'transport'],
  ['boat', 'Boat', 'transport'],
  ['flight', 'Flight', 'transport'],
  ['helicopter', 'Helicopter', 'transport'],
  ['road', 'Road', 'transport'],
  // nature
  ['earth', 'Earth', 'nature'],
  ['star', 'Star', 'nature'],
  ['sun', 'Sun', 'nature'],
  ['plant', 'Plant', 'nature'],
  ['forest', 'Forest', 'nature'],
  ['hillStation', 'Hill Station', 'nature'],
  ['solarPanel', 'Solar Panel', 'nature'],
  ['map', 'Map', 'nature'],
  // devices
  ['laptop', 'Laptop', 'devices'],
  ['gps', 'GPS', 'devices'],
  ['phoneTower', 'Phone Tower', 'devices'],
  ['wifi', 'Wi‑Fi', 'devices'],
  ['light', 'Light', 'devices'],
  ['bluetooth', 'Bluetooth', 'devices'],
  ['umbrella', 'Umbrella', 'devices'],
  // money / bio (project & security)
  ['dollar', 'Dollar', 'project'],
  ['euro', 'Euro', 'project'],
  ['rupee', 'Rupee', 'project'],
  ['envelope', 'Envelope', 'notify'],
  ['virus', 'Virus', 'security'],
  ['bug', 'Bug', 'security'],
  ['insect', 'Insect', 'security'],
];
